import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 20, 30, 50],
}) {
  const [isPageSizeOpen, setIsPageSizeOpen] = useState(false);
  const pageSizeRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (pageSizeRef.current && !pageSizeRef.current.contains(event.target)) {
        setIsPageSizeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  if (totalPages === 0) return null;

  return (
    <div className="flex items-center gap-3 text-sm font-medium text-slate-600 dark:text-slate-400 flex-wrap">
      <div className="flex h-10 items-center gap-1 bg-white dark:bg-slate-800 rounded-[2rem] px-1.5 py-0 shadow-sm border border-slate-200 dark:border-slate-700">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-slate-800 dark:text-slate-200"
        >
          <ChevronLeft size={18} />
        </button>
        
        {getPageNumbers().map((page, index) => (
          <button
            key={index}
            onClick={() => typeof page === 'number' && onPageChange(page)}
            disabled={page === '...'}
            className={`min-w-[30px] h-[30px] px-1 flex items-center justify-center rounded-full transition-colors ${
              page === currentPage
                ? "bg-blue-100/50 text-[#1d57a5] border border-[#1d57a5]/60 dark:bg-blue-900/30 dark:text-[#4a8ce3] dark:border-[#4a8ce3]/60" 
                : typeof page === 'number' ? "hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200" : "cursor-default text-slate-400"
            }`}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-slate-800 dark:text-slate-200"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="relative shrink-0" ref={pageSizeRef}>
        <button
          type="button"
          onClick={() => setIsPageSizeOpen((prev) => !prev)}
          className="h-10 min-w-[6.5rem] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full pl-3 pr-7 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-[#1d57a5]/50 shadow-sm cursor-pointer flex items-center"
        >
          {itemsPerPage} / page
          <ChevronDown
            size={16}
            className={`absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-800 dark:text-slate-200 transition-transform ${isPageSizeOpen ? "rotate-180" : ""}`}
          />
        </button>
        {isPageSizeOpen && (
          <div className="absolute z-20 mt-2 min-w-[6.5rem] overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg">
            {itemsPerPageOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  onItemsPerPageChange(option);
                  setIsPageSizeOpen(false);
                }}
                className={`w-full px-3.5 py-2 text-left text-sm transition-colors ${
                  itemsPerPage === option
                    ? "bg-[#1d57a5] text-white"
                    : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                }`}
              >
                {option} / page
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
